import { AxiosError } from "axios";
import apiClient, { apiUrl } from "../../services/apiClient";
import { logError } from "../../services/logging";
import { devLog, getUUID } from "../../services/util";
import {
  getAuthToken,
  getDecodedToken,
  isLoggedIn,
} from "../accounts/auth.service";
import { EventRecord } from "./analytics";

// Flush reasons that mean the page may be about to go away (see LogProvider)
const LEAVING_REASONS = new Set(["beforeunload", "visibilitychange"]);
const KEEPALIVE_MAX_BYTES = 60000; // browsers cap in-flight keepalive bodies at 64 KB
const MAX_QUEUED_EVENTS = 500;
const RETRY_PAUSE_MS = 60000;

class AnalyticsBatcher {
  private eventQueue: EventRecord[] = [];
  private _sessionId: string | null = null;
  private flushTimeout?: number;
  private failedAt = 0;
  private readonly FLUSH_INTERVAL_MS = 300000;
  private readonly BATCH_SIZE = 100;

  constructor() {
    // Session ID is now lazy-loaded to ensure we get the token's session ID when available
  }

  /**
   * Get the current session ID. Prefers the server-generated session ID from the JWT token.
   * Falls back to a client-generated session ID if no token is available (e.g., during signup).
   */
  get sessionId(): string {
    // Always try to get the session ID from the token first (it may have been set after construction)
    const token = getDecodedToken();
    if (token?.sessionId) {
      // If we have a token session ID, use it and update our cached value
      if (this._sessionId !== token.sessionId) {
        this._sessionId = token.sessionId;
      }
      return token.sessionId;
    }

    // Fallback: generate a client-side session ID if no token available
    if (!this._sessionId) {
      this._sessionId = generateFallbackSessionId();
    }
    return this._sessionId;
  }

  /**
   * Reset session ID (call when user logs in to pick up new token session ID)
   */
  refreshSessionId(): void {
    this._sessionId = null;
  }

  queueEvent(event: Omit<EventRecord, "sessionId" | "ts">) {
    if (!event.type) {
      // `internal` only keeps an event out of GA / Intercom / Sentry breadcrumbs. It used to also
      // leave `type` unset, and the server stores untyped events nowhere, so every designer edit,
      // `app/login` and onboarding `step-completed` was dropped (2026-09-23).
      event.type = event.feature || event.tag ? "feature" : "trace";
    }

    if (event.type === "feature" && event.value === undefined) event.value = 1;

    // Truncate label to 100 chars to prevent database truncation errors
    const truncatedLabel = event.label?.substring(0, 100);

    this.eventQueue.push({
      ...event,
      label: truncatedLabel,
      sessionId: this.sessionId,
      ts: new Date().toLocaleTimeString(),
      timestamp: new Date().toISOString(),
    });

    // Flush if queue is full (not within a minute of a failed flush: that is the timer's job)
    const fullQueue =
      this.eventQueue.length >= this.BATCH_SIZE &&
      Date.now() - this.failedAt > RETRY_PAUSE_MS;
    if (fullQueue || event.flush) {
      this.flush(event.flush ? event.category : "batch-full");
      return;
    }

    // Start timeout if not already started
    this.startFlushTimeout();
  }

  private startFlushTimeout() {
    // Don't start a new timeout if one is already running
    if (this.flushTimeout) return;

    // Use a shorter timeout if we have a lot of events already
    const timeoutDuration =
      this.eventQueue.length > this.BATCH_SIZE / 2
        ? this.FLUSH_INTERVAL_MS / 3 // Faster flush for larger queues
        : this.FLUSH_INTERVAL_MS;

    this.flushTimeout = window.setTimeout(() => {
      // Only flush if we have enough events or it's been a while
      if (this.eventQueue.length > 0) {
        this.flush("timeout");
      } else {
        // Just clear the timeout if we have no events
        this.flushTimeout = undefined;
      }
    }, timeoutDuration);
  }

  async flush(reason: string) {
    if (this.eventQueue.length === 0 || !isLoggedIn()) return;

    const events = [...this.eventQueue];
    try {
      this.eventQueue = [];

      // Clear any pending timeout
      if (this.flushTimeout) {
        window.clearTimeout(this.flushTimeout);
        this.flushTimeout = undefined;
      }

      // Safer browser and OS detection
      let userBrowser = navigator.userAgent;
      let browserVersion = "";
      let userOs = "unknown";

      // Use type assertion for the newer API
      const nav = navigator as Navigator & {
        userAgentData?: {
          platform: string;
          brands?: Array<{ brand: string; version: string }>;
        };
      };

      if (nav.userAgentData) {
        // Extract OS information
        userOs = nav.userAgentData.platform || "unknown";

        // Extract the actual browser name and version
        if (nav.userAgentData.brands && nav.userAgentData.brands.length > 0) {
          // Look for recognized browsers in order of preference
          const browserMap = {
            "Google Chrome": "Chrome",
            "Microsoft Edge": "Edge",
            Opera: "Opera",
            Firefox: "Firefox",
            Safari: "Safari",
          };

          // Find the first recognized brand or use the second entry (usually the actual browser)
          for (const brand of nav.userAgentData.brands) {
            if (browserMap[brand.brand]) {
              userBrowser = browserMap[brand.brand];
              browserVersion = brand.version;
              break;
            }
          }

          // If no recognized browser was found, use the second entry
          // (typically Chrome, Edge, etc. - avoiding "Not A;Brand")
          if (
            userBrowser === "unknown" &&
            nav.userAgentData.brands.length > 1
          ) {
            userBrowser = nav.userAgentData.brands[1].brand;
            browserVersion = nav.userAgentData.brands[1].version;
          }
        }
      } else {
        // Legacy approach
        const ua = navigator.userAgent;

        // Basic OS detection
        if (/Windows/.test(ua)) userOs = "Windows";
        else if (/Macintosh|Mac OS X/.test(ua)) userOs = "macOS";
        else if (/Linux/.test(ua)) userOs = "Linux";
        else if (/Android/.test(ua)) userOs = "Android";
        else if (/iPhone|iPad|iPod/.test(ua)) userOs = "iOS";

        // Browser and version detection
        const extractVersion = (regex: RegExp): string => {
          const match = ua.match(regex);
          return match && match.length > 1 ? match[1] : "unknown";
        };

        if (/Firefox\//.test(ua)) {
          userBrowser = "Firefox";
          browserVersion = extractVersion(/Firefox\/([\d.]+)/);
        } else if (/Edg\//.test(ua)) {
          userBrowser = "Edge";
          browserVersion = extractVersion(/Edg\/([\d.]+)/);
        } else if (/Chrome\//.test(ua)) {
          userBrowser = "Chrome";
          browserVersion = extractVersion(/Chrome\/([\d.]+)/);
        } else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
          userBrowser = "Safari";
          // Safari version is typically in Version/ not Safari/
          browserVersion = extractVersion(/Version\/([\d.]+)/);
        } else if (/OPR\//.test(ua)) {
          userBrowser = "Opera";
          browserVersion = extractVersion(/OPR\/([\d.]+)/);
        }
      }

      // Format browser with version
      const formattedBrowser = `${userBrowser} ${browserVersion}`;

      const payload = {
        host: window.location.host,
        sessionId: this.sessionId,
        userBrowser: formattedBrowser,
        userOs,
        events,
        reason,
      };
      const body = JSON.stringify(payload);

      if (LEAVING_REASONS.has(reason) && body.length < KEEPALIVE_MAX_BYTES) {
        // The page is going away: an axios request would be cancelled with it, and this batch holds
        // the user's last minutes, the part an investigation needs most. keepalive lets it finish.
        fetch(apiUrl + "/admin/events", {
          method: "POST",
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body,
        }).catch(() => undefined);
      } else {
        // Analytics batching is non-critical - use longer timeout and swallow errors
        try {
          await apiClient.post("/admin/events", payload, {
            timeout: 120000, // 2 minutes for batch analytics
          });
        } catch (error) {
          console.warn("Analytics batch failed:", error);
          // Put the batch back for the next flush, unless the server rejected it (4xx), which would
          // fail again forever. Capped, and full-queue flushes pause, so an outage cannot loop.
          const status = (error as AxiosError)?.response?.status;
          if (!status || status >= 500 || status === 408 || status === 429) {
            this.eventQueue = [...events, ...this.eventQueue].slice(
              -MAX_QUEUED_EVENTS
            );
            this.failedAt = Date.now();
          }
        }
      }

      devLog("event flush", reason, formattedBrowser, userOs, events.length);

      // Start new timeout for any new events
      this.startFlushTimeout();
    } catch (error) {
      console.error("Failed to send analytics batch:", error);
      logError(error, "Failed to send analytics batch");
      // On error, put events back in queue
      this.eventQueue.unshift(...events);
    }
  }

  // Make flush accessible to LogProvider
  static getInstance() {
    if (!this.instance) {
      this.instance = new AnalyticsBatcher();
    }
    return this.instance;
  }

  private static instance: AnalyticsBatcher;
}

export const queueEvent = (event: Omit<EventRecord, "sessionId" | "ts">) => {
  const analyticsBatcher = AnalyticsBatcher.getInstance();
  analyticsBatcher.queueEvent(event);
  devLog("trackEvent", event);
};

export const saveEventLog = (reason: string) => {
  const analyticsBatcher = AnalyticsBatcher.getInstance();
  analyticsBatcher.flush(reason);
};

export const getSessionId = () => {
  return AnalyticsBatcher.getInstance().sessionId;
};

/**
 * Refresh the session ID to pick up a new token's session ID after login.
 * Call this after setting the auth token.
 */
export const refreshSessionId = () => {
  AnalyticsBatcher.getInstance().refreshSessionId();
};

export const flushIfQueued = (reason: string, minQueueSize: number = 5) => {
  const batcher = AnalyticsBatcher.getInstance();
  // Access private field via bracket notation for conditional flush
  const queueLength = (batcher as any).eventQueue?.length || 0;
  if (queueLength >= minQueueSize) {
    batcher.flush(reason);
  }
};

/**
 * Generate a fallback session ID matching the server format: yyMMddHH-shortguid (20 chars)
 * Example: 25120317-a1b2c3d4e5f
 * Used only when no token session ID is available (e.g., during signup before login completes)
 */
function generateFallbackSessionId(): string {
  const now = new Date();
  const yy = String(now.getUTCFullYear()).slice(2); // Last 2 digits of year
  const MM = String(now.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(now.getUTCDate()).padStart(2, "0");
  const HH = String(now.getUTCHours()).padStart(2, "0");
  const datePart = `${yy}${MM}${dd}${HH}`;

  // Short guid: first 11 chars of a uuid without hyphens (matching server format)
  const shortGuid = getUUID().replace(/-/g, "").slice(0, 11);

  return `${datePart}-${shortGuid}`;
}
